import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { PlusIcon as Plus, MagnifyingGlassIcon as MagnifyingGlass, PencilSimpleIcon as PencilSimple, TrashIcon as Trash, UsersIcon as Users, ShieldCheckIcon as ShieldCheck, WifiHighIcon as WifiHigh, MapPinIcon as MapPin, CurrencyEurIcon as CurrencyEur, ArrowsClockwiseIcon as ArrowsClockwise, CaretDownIcon as CaretDown, CaretUpIcon as CaretUp, CloudIcon as Cloud, PrinterIcon as Printer } from "@phosphor-icons/react";
import { SophosWizard } from "@/components/SophosWizard";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const EMPTY = {
  ragione_sociale: "",
  partita_iva: "",
  codice_fiscale: "",
  indirizzo: "",
  citta: "",
  cap: "",
  email: "",
  telefono: "",
  stato: "attivo",
  domini: "",
  minimo_fatturabile_minuti: 30,
  arrotondamento_minuti: 15,
  tariffa_oraria: 0,
  zona: "",
  metodo_addebito_default_interventi: "",
  note: "",
};

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState("");
  const [stato, setStato] = useState("attivo");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [targets, setTargets] = useState({});
  const [sophosOpen, setSophosOpen] = useState(false);
  const [prtMappingOpen, setPrtMappingOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [mergeSource, setMergeSource] = useState("");
  const [mtQ, setMtQ] = useState("");
  const [msQ, setMsQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const [mergeConfirm, setMergeConfirm] = useState(null); // { tgt, src }
  const [modalitaOpts, setModalitaOpts] = useState([]);
  const [zoneCfg, setZoneCfg] = useState([]);

  useEffect(() => {
    if (!dupOpen || !mergeTarget) { setSuggestions([]); return; }
    setSuggLoading(true);
    api.get(`/clients/${mergeTarget}/merge-suggestions`)
      .then(({ data }) => setSuggestions(data || []))
      .catch(() => setSuggestions([]))
      .finally(() => setSuggLoading(false));
  }, [mergeTarget, dupOpen]);

  useEffect(() => {
    api.get("/interventi-config").then(({ data }) => {
      setModalitaOpts((data?.modalita || []).filter((m) => m.attivo !== false));
      setZoneCfg((data?.zone || []).filter((z) => z.attivo !== false));
    }).catch(() => {});
  }, []);
  const zoneOptions = (current) => {
    const list = zoneCfg.map((z) => z.label);
    if (current && !list.includes(current)) list.unshift(current);
    return list;
  };

  // VIES lookup
  const [viesLoading, setViesLoading] = useState(false);
  const [viesData, setViesData] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const sortVal = (c, key) => {
    switch (key) {
      case "ragione_sociale": return (c.ragione_sociale || "").toLowerCase();
      case "partita_iva": return (c.partita_iva || "").toLowerCase();
      case "citta": return (c.citta || "").toLowerCase();
      case "contatti": return (c.email || c.telefono || "").toLowerCase();
      case "stato": return (c.stato || "").toLowerCase();
      default: return "";
    }
  };
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const DEFAULT_W = { ragione_sociale: 280, partita_iva: 140, citta: 160, contatti: 220, stato: 140, azioni: 100 };
  const [colW, setColW] = useState(() => { try { return { ...DEFAULT_W, ...JSON.parse(localStorage.getItem("clients_col_w") || "{}") }; } catch { return DEFAULT_W; } });
  useEffect(() => { try { localStorage.setItem("clients_col_w", JSON.stringify(colW)); } catch { /* noop */ } }, [colW]);
  const resizing = useRef(null);
  const startResize = (e, key) => {
    e.stopPropagation(); e.preventDefault();
    resizing.current = { key, startX: e.clientX, startW: colW[key] || DEFAULT_W[key] || 120 };
    const onMove = (ev) => { if (!resizing.current) return; const d = ev.clientX - resizing.current.startX; setColW((w) => ({ ...w, [resizing.current.key]: Math.max(50, resizing.current.startW + d) })); };
    const onUp = () => { resizing.current = null; window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };
  const totalW = Object.keys(DEFAULT_W).reduce((s, k) => s + (colW[k] || DEFAULT_W[k]), 0);
  const SortHead = ({ label, k, className }) => (
    <TableHead style={{ width: colW[k] || DEFAULT_W[k] }} className={`relative cursor-pointer select-none hover:text-primary ${className || ""}`} onClick={() => toggleSort(k)} data-testid={`sort-${k}`}>
      <span className={`inline-flex items-center gap-1 ${className?.includes("text-right") ? "justify-end w-full" : ""}`}>{label}{sort.key === k && (sort.dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}</span>
      <span onMouseDown={(e) => startResize(e, k)} onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60" data-testid={`resize-${k}`} />
    </TableHead>
  );
  const sortedClients = (!clients || !sort.key) ? clients : [...clients].sort((a, b) => {
    const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
    if (va < vb) return sort.dir === "asc" ? -1 : 1;
    if (va > vb) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const lookupVies = async () => {
    const vat = (form.partita_iva || "").trim();
    if (!vat) { toast.error("Inserisci prima la Partita IVA"); return; }
    setViesLoading(true);
    try {
      const { data } = await api.get(`/vies/lookup`, { params: { vat } });
      if (!data.valid) { toast.error("Partita IVA non valida o non presente su VIES"); return; }
      if (!data.ragione_sociale && !data.indirizzo) { toast.error("VIES non ha restituito dati fiscali per questa Partita IVA"); return; }
      setViesData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setViesLoading(false); }
  };

  const applyVies = () => {
    if (!viesData) return;
    const citta = [viesData.citta, viesData.provincia].filter(Boolean).join(" ").trim();
    setForm((f) => ({
      ...f,
      ragione_sociale: viesData.ragione_sociale || f.ragione_sociale,
      indirizzo: viesData.indirizzo || f.indirizzo,
      cap: viesData.cap || f.cap,
      citta: citta || f.citta,
    }));
    setViesData(null);
    toast.success("Dati fiscali importati da VIES");
  };

  const openDuplicates = async () => {
    const { data } = await api.get("/clients/duplicates");
    setDupGroups(data);
    const t = {};
    data.forEach((g) => { t[g.partita_iva] = g.clienti[0].id; });
    setTargets(t);
    setDupOpen(true);
  };

  const mergeManual = () => {
    if (!mergeTarget || !mergeSource) { toast.error("Seleziona sia il cliente da mantenere sia quello da unire"); return; }
    if (mergeTarget === mergeSource) { toast.error("Seleziona due clienti diversi"); return; }
    const tgt = (clients || []).find((c) => c.id === mergeTarget);
    const src = (clients || []).find((c) => c.id === mergeSource);
    setMergeConfirm({ tgt, src });
  };

  const doMerge = async () => {
    if (!mergeConfirm) return;
    try {
      await api.post("/clients/merge", { target_id: mergeConfirm.tgt.id, source_ids: [mergeConfirm.src.id] });
      toast.success("Anagrafiche unite correttamente");
      setMergeConfirm(null);
      setMergeTarget(""); setMergeSource(""); setMtQ(""); setMsQ(""); setSuggestions([]);
      load();
      const { data } = await api.get("/clients/duplicates"); setDupGroups(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const mergeGroup = async (g) => {
    const target_id = targets[g.partita_iva];
    const source_ids = g.clienti.map((c) => c.id).filter((id) => id !== target_id);
    try {
      await api.post("/clients/merge", { target_id, source_ids });
      toast.success("Anagrafiche unite");
      const { data } = await api.get("/clients/duplicates");
      setDupGroups(data);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (stato !== "all") params.stato = stato;
    api.get("/clients", { params }).then(({ data }) => setClients(data));
  }, [q, stato]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c, domini: (c.domini || []).join(", ") });
    setOpen(true);
  };

  const save = async () => {
    if (!form.ragione_sociale.trim()) {
      toast.error("La ragione sociale è obbligatoria");
      return;
    }
    try {
      const payload = { ...EMPTY, ...form };
      payload.domini = typeof form.domini === "string"
        ? form.domini.split(",").map((d) => d.trim().toLowerCase()).filter(Boolean)
        : form.domini;
      payload.minimo_fatturabile_minuti = Number(form.minimo_fatturabile_minuti) || 0;
      payload.arrotondamento_minuti = Number(form.arrotondamento_minuti) || 0;
      payload.tariffa_oraria = Number(form.tariffa_oraria) || 0;
      delete payload.id;
      delete payload.created_at;
      delete payload._id;
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Cliente aggiornato" : "Cliente creato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare ${c.ragione_sociale}? Verranno rimossi anche contratti e dispositivi collegati.`))
      return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const importAllSophos = async () => {
    if (!window.confirm("Importare TUTTI i clienti Sophos non ancora presenti in anagrafica?")) return;
    const tid = toast.loading("Importazione clienti Sophos in corso...");
    try {
      const { data } = await api.post("/sophos/import-all");
      toast.success("Import Sophos completato", { id: tid, description: `${data.creati} creati, ${data.saltati} già presenti (su ${data.totale})` });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: tid }); }
  };

  const importAllZyxel = async () => {
    if (!window.confirm("Importare TUTTE le organizzazioni Zyxel Nebula non ancora presenti in anagrafica?")) return;
    const tid = toast.loading("Importazione organizzazioni Zyxel in corso...");
    try {
      const { data } = await api.post("/zyxel/import-all");
      toast.success("Import Zyxel completato", { id: tid, description: `${data.creati} creati, ${data.saltati} già presenti (su ${data.totale})` });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: tid }); }
  };

  const importAllPrintanista = async () => {
    if (!window.confirm("Importare TUTTI gli account Printanista (mds) non ancora presenti in anagrafica?")) return;
    const tid = toast.loading("Importazione account Printanista in corso...");
    try {
      const { data } = await api.post("/printanista/import-all");
      toast.success("Import Printanista completato", { id: tid, description: `${data.creati} creati, ${data.collegati} collegati (nome coincidente), ${data.saltati} già presenti (su ${data.totale})` });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail), { id: tid }); }
  };

  const syncSaas = async (provider, endpoint) => {
    const tid = toast.loading(`Sincronizzazione ${provider} in corso...`);
    try {
      const { data } = await api.post(endpoint);
      if (data.configured === false) {
        toast.warning(`${provider} non configurato`, { id: tid, description: data.message || "Configura il connettore in Impostazioni → Connettori." });
      } else if (data.pending) {
        toast.info(`${provider}: sincronizzazione in sospeso`, { id: tid, description: data.message });
      } else {
        toast.success(`${provider} sincronizzato`, { id: tid, description: data.message || `${data.nuovi || 0} nuovi, ${data.aggiornati || 0} aggiornati` });
        load();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || `Errore sincronizzazione ${provider}`, { id: tid }); }
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="Clienti"
          subtitle="Anagrafica clienti e dati fiscali"
          actions={
            <div className="flex gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="sync-saas-btn" className="rounded-sm gap-2">
                    <ArrowsClockwise size={16} weight="bold" /> Sincronizza contatti da SaaS
                    <CaretDown size={14} className="opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rounded-sm">
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">Sorgenti SaaS</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="sync-sophos-select" onClick={() => setSophosOpen(true)} className="gap-2 cursor-pointer">
                    <ShieldCheck size={15} weight="bold" className="text-emerald-600" /> Sophos — Seleziona clienti
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="sync-sophos-all" onClick={importAllSophos} className="gap-2 cursor-pointer">
                    <ShieldCheck size={15} className="text-emerald-600" /> Sophos — Importa tutti
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="sync-zyxel-all" onClick={importAllZyxel} className="gap-2 cursor-pointer">
                    <WifiHigh size={15} weight="bold" className="text-sky-600" /> Zyxel Nebula — Importa tutti
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="sync-printanista-all" onClick={importAllPrintanista} className="gap-2 cursor-pointer">
                    <Printer size={15} weight="bold" className="text-orange-600" /> Printanista — Importa tutti
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="sync-also" onClick={() => syncSaas("ALSO Cloud Marketplace", "/sync/also-contacts")} className="gap-2 cursor-pointer">
                    <Cloud size={15} weight="bold" className="text-indigo-600" /> ALSO Cloud Marketplace
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" data-testid="merge-btn" onClick={openDuplicates} className="rounded-sm gap-2">
                <Users size={16} weight="bold" /> Unisci duplicati
              </Button>
              <Button data-testid="new-client-btn" onClick={openNew} className="rounded-sm gap-2">
                <Plus size={16} weight="bold" /> Nuovo cliente
              </Button>
            </div>
          }
        />

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              data-testid="client-search"
              placeholder="Cerca per ragione sociale, P.IVA, città..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 rounded-sm bg-white"
            />
          </div>
          <Tabs value={stato} onValueChange={setStato}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="all" data-testid="filter-all">Tutti</TabsTrigger>
              <TabsTrigger value="attivo">Attivi</TabsTrigger>
              <TabsTrigger value="sospeso">Sospesi</TabsTrigger>
              <TabsTrigger value="cessato">Cessati</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Card className="rounded-sm border-border shadow-none">
          <div>
          <Table style={{ tableLayout: "fixed", width: totalW }}>
            <TableHeader>
              <TableRow>
                <SortHead label="Ragione sociale" k="ragione_sociale" />
                <SortHead label="P. IVA" k="partita_iva" />
                <SortHead label="Città" k="citta" />
                <SortHead label="Contatti" k="contatti" />
                <SortHead label="Stato" k="stato" />
                <TableHead style={{ width: colW["azioni"] || DEFAULT_W["azioni"] }} className="relative text-right">Azioni<span onMouseDown={(e) => startResize(e, "azioni")} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                    Nessun cliente trovato.
                  </TableCell>
                </TableRow>
              )}
              {sortedClients?.map((c) => (
                <TableRow key={c.id} data-testid="client-row" className="hover:bg-secondary cursor-pointer" onClick={() => navigate(`/clienti/${c.id}`)}>
                  <TableCell style={{ width: colW["ragione_sociale"] || DEFAULT_W["ragione_sociale"] }} className="font-medium overflow-hidden text-ellipsis whitespace-nowrap">{c.ragione_sociale}</TableCell>
                  <TableCell style={{ width: colW["partita_iva"] || DEFAULT_W["partita_iva"] }} className="tabular-nums text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                    {c.partita_iva || "—"}
                  </TableCell>
                  <TableCell style={{ width: colW["citta"] || DEFAULT_W["citta"] }} className="text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap">{c.citta || "—"}</TableCell>
                  <TableCell style={{ width: colW["contatti"] || DEFAULT_W["contatti"] }} className="text-muted-foreground text-sm overflow-hidden text-ellipsis whitespace-nowrap">{c.email || c.telefono || "—"}</TableCell>
                  <TableCell style={{ width: colW["stato"] || DEFAULT_W["stato"] }} className="overflow-hidden">
                    <StatusBadge status={c.stato} />
                    {(c.source === "printanista" || (c.alias_origini || []).some((a) => a.source === "printanista")) && <span className="ml-1 inline-block rounded-sm text-[10px] bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5" data-testid="client-printanista-badge">Printanista</span>}
                  </TableCell>
                  <TableCell style={{ width: colW["azioni"] || DEFAULT_W["azioni"] }} className="text-right overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid="edit-client-btn"
                      onClick={() => openEdit(c)}
                      className="h-8 w-8"
                    >
                      <PencilSimple size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(c)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    >
                      <Trash size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? "Modifica cliente" : "Nuovo cliente"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Ragione sociale *</Label>
              <Input data-testid="client-ragione-sociale" value={form.ragione_sociale} onChange={set("ragione_sociale")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Partita IVA</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="client-partita-iva" value={form.partita_iva} onChange={set("partita_iva")} className="rounded-sm" placeholder="es. IT00905811006" />
                <Button type="button" variant="outline" size="sm" onClick={lookupVies} disabled={viesLoading} className="rounded-sm shrink-0 gap-1" data-testid="vies-compila-btn">
                  <MagnifyingGlass size={14} weight="bold" /> {viesLoading ? "…" : "Compila"}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Recupera ragione sociale e indirizzo dal registro VIES (UE).</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Codice Fiscale</Label>
              <Input value={form.codice_fiscale} onChange={set("codice_fiscale")} className="mt-1 rounded-sm" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Indirizzo</Label>
              <Input value={form.indirizzo} onChange={set("indirizzo")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Città</Label>
              <Input value={form.citta} onChange={set("citta")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">CAP</Label>
              <Input value={form.cap} onChange={set("cap")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input value={form.email} onChange={set("email")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Telefono</Label>
              <Input value={form.telefono} onChange={set("telefono")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm((f) => ({ ...f, stato: v }))}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="cessato">Cessato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Domini email (per abbinamento ticket, separati da virgola)</Label>
              <Input data-testid="client-domini" value={form.domini} onChange={set("domini")} className="mt-1 rounded-sm" placeholder="es. rossistudio.it, rossi.eu" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Minimo fatturabile (min)</Label>
              <Input type="number" data-testid="client-minimo" value={form.minimo_fatturabile_minuti} onChange={set("minimo_fatturabile_minuti")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Arrotondamento (min)</Label>
              <Input type="number" value={form.arrotondamento_minuti} onChange={set("arrotondamento_minuti")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Tariffa oraria (€)</Label>
              <Input type="number" value={form.tariffa_oraria} onChange={set("tariffa_oraria")} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5"><MapPin size={14} className="text-muted-foreground" /> Zona</Label>
              <Select value={form.zona || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, zona: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="client-zona"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {zoneOptions(form.zona).map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
              {zoneCfg.length === 0 && <p className="text-[10px] text-muted-foreground mt-1">Configura le zone in Impostazioni → Interventi.</p>}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5"><CurrencyEur size={14} className="text-muted-foreground" /> Metodo addebito default interventi</Label>
              <Select value={form.metodo_addebito_default_interventi || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, metodo_addebito_default_interventi: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="client-metodo-addebito"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuno</SelectItem>
                  {modalitaOpts.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Note</Label>
              <Textarea value={form.note} onChange={set("note")} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-client-btn" onClick={save} className="rounded-sm">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Unione anagrafiche</DialogTitle></DialogHeader>

          <div className="border border-primary/30 bg-primary/5 rounded-sm p-4 mb-3" data-testid="manual-merge">
            <div className="text-sm font-semibold mb-2">Unione manuale</div>
            <p className="text-xs text-muted-foreground mb-3">Scegli tu quali due clienti unire. Il "cliente da unire" verrà eliminato e i suoi dati spostati sul "cliente da mantenere".</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Cliente da mantenere</label>
                <Input placeholder="Cerca..." value={mtQ} onChange={(e) => setMtQ(e.target.value)} className="rounded-sm mt-1 mb-1 h-8 bg-white" data-testid="merge-target-search" />
                <Select value={mergeTarget} onValueChange={setMergeTarget}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="merge-target-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    {(clients || []).filter((c) => !mtQ || c.ragione_sociale.toLowerCase().includes(mtQ.toLowerCase())).slice(0, 100).map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}{c.partita_iva ? ` · ${c.partita_iva}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Cliente da unire (eliminato)</label>
                <Input placeholder="Cerca..." value={msQ} onChange={(e) => setMsQ(e.target.value)} className="rounded-sm mt-1 mb-1 h-8 bg-white" data-testid="merge-source-search" />
                <Select value={mergeSource} onValueChange={setMergeSource}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="merge-source-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    {(clients || []).filter((c) => c.id !== mergeTarget && (!msQ || c.ragione_sociale.toLowerCase().includes(msQ.toLowerCase()))).slice(0, 100).map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}{c.partita_iva ? ` · ${c.partita_iva}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button size="sm" onClick={mergeManual} disabled={!mergeTarget || !mergeSource} className="rounded-sm mt-3" data-testid="merge-manual-btn">Unisci i due clienti</Button>

            {mergeTarget && (
              <div className="mt-4 border-t border-primary/20 pt-3" data-testid="merge-suggestions">
                <div className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Users size={13} /> Possibili duplicati suggeriti {suggLoading && <span className="text-muted-foreground font-normal">· ricerca…</span>}</div>
                {!suggLoading && suggestions.length === 0 && <p className="text-xs text-muted-foreground">Nessun duplicato suggerito per il cliente selezionato.</p>}
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {suggestions.map((s) => (
                    <button key={s.id} type="button" onClick={() => { setMergeSource(s.id); setMsQ(s.ragione_sociale); }} data-testid={`merge-suggestion-${s.id}`}
                      className={`w-full text-left rounded-sm border px-2.5 py-2 transition-colors ${mergeSource === s.id ? "border-primary bg-primary/10" : "border-border bg-white hover:border-primary/50"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">{s.ragione_sociale}{s.partita_iva ? ` · ${s.partita_iva}` : ""}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm shrink-0 ${s.confidenza === "alta" ? "bg-red-100 text-red-700" : s.confidenza === "media" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600"}`}>{s.confidenza}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {s.reasons.map((r, i) => <span key={i} className="text-[10px] bg-secondary text-secondary-foreground px-1.5 py-0.5 rounded-sm">{r}</span>)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="text-sm font-semibold mb-1">Duplicati rilevati per Partita IVA</div>
          <p className="text-xs text-muted-foreground">Raggruppate per Partita IVA. Scegli l'anagrafica da mantenere: contratti, dispositivi, ticket, interventi e accessi verranno spostati su di essa.</p>
          {dupGroups.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun duplicato rilevato.</p>}
          {dupGroups.map((g) => (
            <div key={g.partita_iva} className="border border-border rounded-sm p-3 mb-2" data-testid="dup-group">
              <div className="text-xs text-muted-foreground mb-2">P. IVA {g.partita_iva} · {g.clienti.length} anagrafiche</div>
              <Select value={targets[g.partita_iva]} onValueChange={(v) => setTargets((t) => ({ ...t, [g.partita_iva]: v }))}>
                <SelectTrigger className="rounded-sm mb-2"><SelectValue /></SelectTrigger>
                <SelectContent>{g.clienti.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale} ({c.citta || "—"})</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" data-testid="merge-group-btn" onClick={() => mergeGroup(g)} className="rounded-sm">Unisci su questa</Button>
            </div>
          ))}
          <DialogFooter><Button variant="outline" onClick={() => setDupOpen(false)} className="rounded-sm">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <SophosWizard open={sophosOpen} setOpen={setSophosOpen} onImported={load} />
      {/* al termine dell'associazione ricarico i clienti per riflettere gli alias */}

      {/* Conferma unione anagrafiche */}
      <Dialog open={!!mergeConfirm} onOpenChange={(o) => { if (!o) setMergeConfirm(null); }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="merge-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading">Conferma unione anagrafiche</DialogTitle></DialogHeader>
          {mergeConfirm && (
            <div className="py-2 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-emerald-300 bg-emerald-50/50 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Mantenuto</div>
                  <div className="text-sm font-medium" data-testid="merge-keep-name">{mergeConfirm.tgt.ragione_sociale}</div>
                  <div className="text-xs text-muted-foreground mt-1">{mergeConfirm.tgt.partita_iva || "—"} · {mergeConfirm.tgt.citta || "—"}</div>
                  <div className="text-xs text-muted-foreground">{mergeConfirm.tgt.email || "—"}</div>
                </div>
                <div className="border border-red-300 bg-red-50/50 rounded-sm p-3">
                  <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-1">Unito → eliminato</div>
                  <div className="text-sm font-medium" data-testid="merge-drop-name">{mergeConfirm.src.ragione_sociale}</div>
                  <div className="text-xs text-muted-foreground mt-1">{mergeConfirm.src.partita_iva || "—"} · {mergeConfirm.src.citta || "—"}</div>
                  <div className="text-xs text-muted-foreground">{mergeConfirm.src.email || "—"}</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground bg-secondary/60 rounded-sm p-2.5">
                Tutti i dati (contratti, dispositivi, ticket, interventi, servizi, accessi) e gli <b>identificativi esterni</b> (Sophos, Microsoft CSP, Zyxel…) del cliente unito verranno salvati sul cliente mantenuto in <b>Origini collegate</b>. I futuri import non ricreeranno il duplicato.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setMergeConfirm(null)} data-testid="merge-confirm-cancel">Annulla</Button>
            <Button className="rounded-sm" onClick={doMerge} data-testid="merge-confirm-ok">Conferma unione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma dati VIES */}
      <Dialog open={!!viesData} onOpenChange={(o) => { if (!o) setViesData(null); }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="vies-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ShieldCheck size={18} weight="bold" className="text-primary" /> Dati trovati su VIES</DialogTitle>
          </DialogHeader>
          {viesData && (
            <div className="py-2 space-y-3">
              <p className="text-sm text-muted-foreground">Controlla i dati recuperati dal registro VIES e conferma per compilare la scheda cliente.</p>
              <div className="border border-border rounded-sm divide-y divide-border text-sm">
                <div className="flex px-3 py-2"><span className="w-32 text-muted-foreground">Ragione sociale</span><span className="font-medium flex-1" data-testid="vies-rs">{viesData.ragione_sociale || "—"}</span></div>
                <div className="flex px-3 py-2"><span className="w-32 text-muted-foreground">Partita IVA</span><span className="flex-1">{viesData.country} {viesData.vat_number}</span></div>
                <div className="flex px-3 py-2"><span className="w-32 text-muted-foreground">Indirizzo</span><span className="flex-1" data-testid="vies-indirizzo">{viesData.indirizzo || "—"}</span></div>
                <div className="flex px-3 py-2"><span className="w-32 text-muted-foreground">CAP</span><span className="flex-1">{viesData.cap || "—"}</span></div>
                <div className="flex px-3 py-2"><span className="w-32 text-muted-foreground">Città</span><span className="flex-1">{[viesData.citta, viesData.provincia].filter(Boolean).join(" ") || "—"}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setViesData(null)} data-testid="vies-cancel-btn">Annulla</Button>
            <Button className="rounded-sm gap-2" onClick={applyVies} data-testid="vies-confirm-btn"><ShieldCheck size={16} weight="bold" /> Conferma e compila</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
